import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable, filter, take } from 'rxjs';
import { AgentService, AgentDto, ZoneDto, AgentDtoPaginatedResponse } from '../../services/agent.service';
import { AuthService } from '../../services/auth.service';
import { ExternalDataService, PricingGroup } from '../../services/externalDataService.service';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { CreateAgentComponent } from './create-agent/create-agent.component';
import { AssignAgentZoneComponent } from './assign-agent-zone/assign-agent-zone.component';
import { AssignAgentPricingGroupComponent } from './assign-agent-pricing-group/assign-agent-pricing-group.component';
import { AgentAvatarComponent } from './agent-avatar/agent-avatar.component';
import { AgentProfilePictureEditorComponent } from './agent-profile-picture-editor/agent-profile-picture-editor.component';
import { AgentProfilePictureService } from '../../services/agent-profile-picture.service';
import { AgentPictureUrls } from './agent-picture-urls';

@Component({
  selector: 'app-agents',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, PaginationComponent, CreateAgentComponent, AssignAgentZoneComponent, AssignAgentPricingGroupComponent, AgentAvatarComponent, AgentProfilePictureEditorComponent],
  templateUrl: './agents.component.html',
  styleUrl: './agents.component.css'
})
export class AgentsComponent implements OnInit, OnDestroy {
  agents$: Observable<AgentDtoPaginatedResponse> | undefined;
  zones: ZoneDto[] = [];
  expandedRows: Set<string> = new Set();
  showCreateModal = false;
  showAssignZoneModal = false;
  showAssignPricingGroupModal = false;
  showStatusModal = false;
  selectedAgent: AgentDto | null = null;
  pricingGroups: PricingGroup[] = [];
  /** Profile picture links for agents on the current page. */
  readonly pictures: AgentPictureUrls;
  
  // Notification properties
  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };
  
  // Inline Removal State
  removingZoneState: string | null = null; // format: "agentId:zoneId"

  // Filter properties
  selectedZoneFilter: string = '';
  statusFilter: string = ''; // '', 'active', 'inactive'
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount: number = 0;
  totalPages: number = 1;
  
  // Loading states
  isLoading = false;
  isToggling: Set<string> = new Set();
  
  // Make Math available in template
  Math = Math;

  constructor(
    private agentService: AgentService,
    private authService: AuthService,
    private externalDataService: ExternalDataService,
    private pictureService: AgentProfilePictureService
  ) {
    this.pictures = new AgentPictureUrls(pictureService);
  }

  ngOnInit(): void {
    // Wait for MSAL to be initialized before loading agents
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadAgents();
        this.loadZones();
        this.loadPricingGroups();
      });
  }

  loadAgents(): void {
    this.isLoading = true;
    // Convert page number to page index (page index is 0-based)
    const pageIndex = this.currentPage - 1;
    
    // Convert status filter to boolean if needed
    let isActive: boolean | undefined = undefined;
    if (this.statusFilter === 'active') {
      isActive = true;
    } else if (this.statusFilter === 'inactive') {
      isActive = false;
    }

    this.agents$ = this.agentService.getAgentList(
      this.selectedZoneFilter || undefined,
      isActive,
      pageIndex,
      this.pageSize
    );

    const pictureSeq = this.pictures.begin();
    this.agents$.subscribe({
      next: (response) => {
        if (response) {
          this.pictures.load(response.items || [], pictureSeq);
          this.totalCount = response.totalCount || 0;
          this.calculateTotalPages();
        } else {
          this.totalCount = 0;
          this.totalPages = 1;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading agents:', error);
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.pictures.dispose();
  }

  loadZones(): void {
    this.agentService.getAllZones().subscribe({
      next: (zones) => {
        this.zones = zones;
      },
      error: (error) => {
        console.error('Error loading zones:', error);
      }
    });
  }

  calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.expandedRows.clear(); // Clear expanded rows when changing pages
    this.loadAgents();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1; // Reset to first page when changing page size
    this.expandedRows.clear(); // Clear expanded rows when changing page size
    this.loadAgents();
  }

  onFilterChange(): void {
    this.currentPage = 1; // Reset to first page when changing filters
    this.expandedRows.clear(); // Clear expanded rows when changing filters
    this.loadAgents();
  }

  toggleRow(agentId: string): void {
    if (this.expandedRows.has(agentId)) {
      this.expandedRows.delete(agentId);
    } else {
      this.expandedRows.add(agentId);
    }
  }

  isRowExpanded(agentId: string): boolean {
    return this.expandedRows.has(agentId);
  }

  getZoneName(zoneId: string): string {
    const zone = this.zones.find(z => z.id === zoneId);
    return zone ? zone.name : zoneId;
  }

  getZoneNames(zoneIds: string[]): string {
    if (!zoneIds || zoneIds.length === 0) {
      return 'No zones assigned';
    }
    return zoneIds.map(id => this.getZoneName(id)).join(', ');
  }

  toggleAgentStatus(agent: AgentDto): void {
    if (this.isToggling.has(agent.agentId)) {
      return; // Prevent multiple simultaneous toggles
    }

    const action = agent.isActive ? 'disable' : 'enable';
    
    if (confirm(`Are you sure you want to ${action} agent "${agent.name}"?`)) {
      this.isToggling.add(agent.agentId);
      this.agentService.toggleAgentStatus(agent).subscribe({
        next: (updatedAgent) => {
          this.showNotification(`Agent "${agent.name}" ${action}d successfully`, 'success');
          this.loadAgents(); // Refresh the list
          this.isToggling.delete(agent.agentId);
        },
        error: (error) => {
          console.error(`Error ${action}ing agent:`, error);
          this.showNotification(`Failed to ${action} agent. Please try again.`, 'error');
          this.isToggling.delete(agent.agentId);
        }
      });
    }
  }

  isAgentToggling(agentId: string): boolean {
    return this.isToggling.has(agentId);
  }

  openCreateModal(): void {
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  onAgentCreated(): void {
    this.closeCreateModal();
    this.loadAgents(); // Refresh the list
  }

  openAssignZoneModal(agent: AgentDto): void {
    this.selectedAgent = agent;
    this.showAssignZoneModal = true;
  }

  closeAssignZoneModal(): void {
    this.showAssignZoneModal = false;
    this.selectedAgent = null;
  }

  onZoneAssigned(): void {
    this.closeAssignZoneModal();
    this.loadAgents(); // Refresh the list
  }

  loadPricingGroups(): void {
    this.externalDataService.getPricingGroups().subscribe({
      next: (groups) => {
        this.pricingGroups = groups;
      },
      error: (err) => console.error('Error loading pricing groups:', err)
    });
  }

  getPricingGroupName(pricingGroupId?: string): string {
    if (!pricingGroupId || pricingGroupId === 'null' || pricingGroupId === 'undefined') return 'Global';
    const group = this.pricingGroups.find(g => g.id === pricingGroupId);
    return group ? group.name : pricingGroupId;
  }

  openAssignPricingGroupModal(agent: AgentDto): void {
    this.selectedAgent = agent;
    this.showAssignPricingGroupModal = true;
  }

  closeAssignPricingGroupModal(): void {
    this.showAssignPricingGroupModal = false;
    this.selectedAgent = null;
  }

  onPricingGroupAssigned(): void {
    this.closeAssignPricingGroupModal();
    this.loadAgents();
  }

  removePricingGroup(agent: AgentDto, event: Event): void {
    event.stopPropagation();
    if (confirm(`Are you sure you want to remove the pricing group from agent "${agent.name}"?`)) {
      this.agentService.removeAgentPricingGroup(agent.agentId).subscribe({
        next: () => {
          this.showNotification(`Pricing group removed from agent ${agent.name}`, 'success');
          this.loadAgents();
        },
        error: (err) => {
          console.error('Error removing pricing group:', err);
          this.showNotification('Failed to remove pricing group.', 'error');
        }
      });
    }
  }

  // Zone Removal - Inline "Double-State" logic
  isZoneRemoving(agentId: string, zoneId: string): boolean {
    return this.removingZoneState === `${agentId}:${zoneId}`;
  }

  startZoneRemoval(agentId: string, zoneId: string, event: Event): void {
    event.stopPropagation();
    this.removingZoneState = `${agentId}:${zoneId}`;
  }

  cancelZoneRemoval(event: Event): void {
    event.stopPropagation();
    this.removingZoneState = null;
  }

  confirmZoneRemoval(agent: AgentDto, zoneId: string, event: Event): void {
    event.stopPropagation();
    const zoneName = this.getZoneName(zoneId);
    
    this.agentService.removeAgentZone({
      agentId: agent.agentId,
      zoneId: zoneId
    }).subscribe({
      next: () => {
        this.showNotification(`Zone "${zoneName}" removed from ${agent.name}`, 'success');
        this.removingZoneState = null;
        this.loadAgents();
      },
      error: (error) => {
        console.error('Error removing zone from agent:', error);
        this.showNotification('Failed to remove zone. Please try again.', 'error');
        this.removingZoneState = null;
      }
    });
  }

  // Deprecated: Old modal-based approach
  removeAgentZone(agent: AgentDto, zoneId: string): void {
    // This is now handled by the start/confirm states
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.notification = {
      show: true,
      message,
      type
    };
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (this.notification.message === message) {
        this.notification.show = false;
      }
    }, 3000);
  }

  closeNotification(): void {
    this.notification.show = false;
  }

  getStatusBadgeClass(isActive: boolean): string {
    return isActive ? 'status-badge active' : 'status-badge inactive';
  }

  getStatusText(isActive: boolean): string {
    return isActive ? 'Active' : 'Inactive';
  }

  getToggleButtonClass(isActive: boolean): string {
    return isActive ? 'toggle-btn disable' : 'toggle-btn enable';
  }

  getToggleButtonText(isActive: boolean): string {
    return isActive ? 'Disable' : 'Enable';
  }
}
